import { useRefreshTokenMutation, useRefreshMobileTokenMutation } from 'store/services/core/login';
import { useLazyStartImpersonationQuery, useLazyStopImpersonationQuery, useLazyStartSubstitutionQuery } from 'store/services/core/user';
import { jwtDecode } from 'jwt-decode';
import { createContext, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useStorage, useStore } from '.';
import { setAuth } from 'store/slices';
import type { RootState } from 'store';
import { LoadingContainer } from 'components/common/LoadingContainer';
import type { STORAGE_VALUES } from 'types/storage';
import { STORAGE_KEYS } from 'types/storage';
import type { STORE_VALUES } from 'types/store';
import { STORE_KEYS } from 'types/store';

import { coreApi } from 'store/services/core';
import { documentStorageApi } from 'store/services/documentStorage';
import { flowApi } from 'store/services/flow';
import { heliosApi } from 'store/services/helios';
import { AuthFlow, ReactNativeMessageType, type AuthState } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import { resolveIdentitySwitchAuth } from 'utils/impersonationAuth';
import { parseHeliosError } from 'utils/helios/parseHeliosError';
import { createPostLoginRedirect } from 'utils/postLoginRedirect';
import { getKeycloakConfig } from 'utils/auth/keycloak/config';
import { mapTokenResponseToAuthState } from 'utils/auth/keycloak/external';
import { exchangeRefreshedInternalSession } from 'utils/auth/keycloak/internal';
import { refreshKeycloakTokens } from 'utils/auth/keycloak/tokens';
import { createLogoutRedirect, isLegacyOauthSession, logoutStoredSession } from 'utils/auth/session';
import { ACCESS_DENIED_PATH, hasAccessToThisDeployment } from 'utils/auth/tenantAccess';
import { getAuthenticatedHomePath, getRoleClaims, isExternalUser } from 'utils/auth/userType';
import { getUnauthenticatedLoginPath, isLoginPath } from 'utils/navigation/urls';

type JWTToken = {
  user_id: string;
  email: string;
  name?: string;
  full_name: string;
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string | string[];
  user_type?: string;
  tenant_id: string;
  tenant_ids?: string[];
  exp: number;
  iss: string;
  aud: string;
};

type LoginContextType = {
  handleLogin: (jwtToken: AuthState, stayLoggedIn: boolean, loginViaStore?: boolean, isImpersonation?: boolean) => void;
  handleLogout: () => void;
  handleImpersonate: (id: string) => void;
  handleUnimpersonate: () => void;
  handleSubstitute: (id: string) => void;
  handleCancelSubstitution: () => void;
  user: JWTToken | null;
  permissions?: {
    userAdmin: boolean;
    templateAdmin: boolean;
    settingAdmin: boolean;
  };
  isAdmin?: boolean;
  isExternal: boolean;
  isImpersonating: boolean;
  isSubstituting: boolean;
};

const LoginContext = createContext<LoginContextType>({
  handleLogin: () => {},
  handleLogout: () => {},
  handleImpersonate: () => {},
  handleUnimpersonate: () => {},
  handleSubstitute: () => {},
  handleCancelSubstitution: () => {},
  user: null,
  isExternal: false,
  isImpersonating: false,
  isSubstituting: false,
});

const adminRoles = ['UserAdmin', 'TemplateAdmin', 'SettingAdmin'];

const LoginProvider = memo(({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileApp = isMobileApp();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [shouldBeLogin, setShouldBeLogin] = useState(true);

  const { getItem: getStoreItem, setItem: setStoreItem, clearItem: clearStoreItem, clearEntireStore } = useStore();
  const { getItem: getStorageItem, setItem: setStorageItem, clearItem: clearStorageItem } = useStorage();

  const [refreshTokenLoginTrigger] = useRefreshTokenMutation();
  const [refreshMobileToken] = useRefreshMobileTokenMutation();

  const jwtToken = useSelector((state: RootState) => state.auth.jwtToken);

  const stateParam = new URL(window.location.href).searchParams.get('state');

  let state: {
    reason: string;
    errorCode: number;
    currentLocation: string;
  } | null = null;

  if (stateParam) {
    try {
      const decodedStateParam = decodeURIComponent(stateParam);
      state = JSON.parse(decodedStateParam);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse state parameter:', error);
    }
  }

  const [user, setUser] = useState<JWTToken | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isSubstituting, setIsSubstituting] = useState(false);

  const [triggerStopImpersonation] = useLazyStopImpersonationQuery();
  const [triggerStartImpersonation] = useLazyStartImpersonationQuery();
  const [triggerSubstitute] = useLazyStartSubstitutionQuery();

  const [permissions, setPermissions] = useState<{
    userAdmin: boolean;
    templateAdmin: boolean;
    settingAdmin: boolean;
  }>({
    userAdmin: false,
    templateAdmin: false,
    settingAdmin: false,
  });

  const checkLogin = (): [boolean, STORE_VALUES['auth'] | null] => {
    const auth = getStoreItem(STORE_KEYS.AUTH);

    return [Boolean(auth?.jwtToken), auth];
  };

  const checkRefreshToken = (): [boolean, STORAGE_VALUES['refreshToken'] | null] => {
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);

    return [!!refreshToken, refreshToken];
  };

  const savePostLoginRedirect = (url: string) => {
    const redirect = createPostLoginRedirect(url, window.location.origin);

    if (redirect) {
      setStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT, redirect);
      return;
    }

    try {
      const parsedUrl = new URL(url);

      if (parsedUrl.origin === window.location.origin && parsedUrl.pathname === '/') {
        clearStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);
      }
    } catch {
      return;
    }
  };

  const bounceUnauthenticatedUser = (redirectUrl?: string) => {
    setShouldBeLogin(false);

    if (window.location.pathname === ACCESS_DENIED_PATH) {
      return;
    }

    if (redirectUrl) {
      savePostLoginRedirect(redirectUrl);
    }

    navigate(getUnauthenticatedLoginPath(window.location.pathname));
  };

  const clearAuthSession = () => {
    clearStoreItem(STORE_KEYS.AUTH);
    clearStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
    dispatch(
      setAuth({
        jwtToken: null,
        refreshToken: null,
        tokenProvider: null,
        apiKey: null,
      })
    );
  };

  const handleLogin = (auth: AuthState, stayLoggedIn: boolean, isLoggedViaStore?: boolean, isImpersonation?: boolean, isSubstitution?: boolean) => {
    if (!auth?.jwtToken) return;

    setIsLoggingIn(true);

    const decodedToken: JWTToken = jwtDecode(auth.jwtToken ?? '');

    if (!hasAccessToThisDeployment(decodedToken)) {
      setUser(null);
      setStoreItem(STORE_KEYS.AUTH, {
        jwtToken: null,
        refreshToken: null,
        tokenProvider: auth.tokenProvider ?? null,
        apiKey: null,
        idToken: auth.idToken ?? null,
      });
      clearStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
      dispatch(
        setAuth({
          jwtToken: null,
          refreshToken: null,
          tokenProvider: null,
          apiKey: null,
        })
      );
      setIsLoggingIn(false);
      setShouldBeLogin(false);
      navigate(ACCESS_DENIED_PATH, { replace: true });
      return;
    }

    const userRoles = getRoleClaims(decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);

    setUser(decodedToken);

    setPermissions({
      userAdmin: userRoles.includes('UserAdmin'),
      templateAdmin: userRoles.includes('TemplateAdmin'),
      settingAdmin: userRoles.includes('SettingAdmin'),
    });

    if (isImpersonation || isSubstitution) {
      const [, oldAuth] = checkLogin();
      const nextAuth = resolveIdentitySwitchAuth(auth, oldAuth);

      dispatch(setAuth(nextAuth));
      setStoreItem(STORE_KEYS.AUTH, nextAuth);
    } else {
      dispatch(setAuth(auth));
      setStoreItem(STORE_KEYS.AUTH, auth);
    }

    if (stayLoggedIn && auth.authFlow === AuthFlow.Internal && auth.keycloakRefreshToken) {
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, {
        refreshToken: auth.keycloakRefreshToken,
        provider: auth.tokenProvider,
        authFlow: AuthFlow.Internal,
      });
    } else if (stayLoggedIn && auth.refreshToken) {
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, {
        refreshToken: auth.refreshToken,
        provider: auth.tokenProvider,
        authFlow: auth.authFlow,
      });
    }

    setIsLoggingIn(false);
    setShouldBeLogin(false);

    const isImpersonateUserAdmin = adminRoles.some((role) => userRoles.includes(role));

    if (isLoggedViaStore || isLoginPath(window.location.pathname)) {
      navigate(getAuthenticatedHomePath(decodedToken.user_type, isImpersonateUserAdmin), { replace: true });
    }
  };

  const loginViaRefreshToken = async () => {
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
    const storedAuth = getStoreItem(STORE_KEYS.AUTH);
    const isImpersonate = getStoreItem(STORE_KEYS.IS_IMPERSONATING);
    const isSubstitute = getStoreItem(STORE_KEYS.IS_SUBSTITUTING);

    if (isImpersonate) {
      setStoreItem(STORE_KEYS.IS_IMPERSONATING, false);
      setIsImpersonating(false);
    }

    if (isSubstitute) {
      setStoreItem(STORE_KEYS.IS_SUBSTITUTING, false);
      setIsSubstituting(false);
    }

    let internalKeycloakRefresh: string | null = null;

    if (storedAuth?.authFlow === AuthFlow.Internal) {
      internalKeycloakRefresh = storedAuth.keycloakRefreshToken ?? null;
    } else if (refreshToken?.authFlow === AuthFlow.Internal) {
      internalKeycloakRefresh = refreshToken.refreshToken;
    }

    if (!refreshToken && !internalKeycloakRefresh) {
      bounceUnauthenticatedUser(window.location.href);
      return false;
    }

    const apiKey = storedAuth?.apiKey ?? process.env.API_KEY ?? '';
    const isLoggedViaStore = isLoginPath(window.location.pathname);

    if (internalKeycloakRefresh) {
      try {
        const nextAuth = await exchangeRefreshedInternalSession(internalKeycloakRefresh, apiKey, storedAuth?.idToken);

        handleLogin(nextAuth, Boolean(refreshToken), isLoggedViaStore);
        return true;
      } catch {
        clearAuthSession();
        bounceUnauthenticatedUser(window.location.href);
        return false;
      }
    }

    if (!refreshToken) {
      bounceUnauthenticatedUser(window.location.href);
      return false;
    }

    if (!isLegacyOauthSession({ tokenProvider: refreshToken.provider })) {
      try {
        const tokens = await refreshKeycloakTokens(refreshToken.refreshToken, getKeycloakConfig());
        const nextAuth = mapTokenResponseToAuthState(tokens, apiKey);

        handleLogin(
          {
            ...nextAuth,
            refreshToken: nextAuth.refreshToken ?? refreshToken.refreshToken,
            idToken: nextAuth.idToken ?? storedAuth?.idToken,
          },
          true,
          isLoggedViaStore
        );
        return true;
      } catch {
        clearAuthSession();
        bounceUnauthenticatedUser(window.location.href);
        return false;
      }
    }

    dispatch(
      setAuth({
        refreshToken: refreshToken.refreshToken,
        tokenProvider: refreshToken.provider,
        jwtToken: storedAuth?.jwtToken ?? '',
        apiKey,
      })
    );

    const response = await (mobileApp && refreshToken.provider === 'Google' ? refreshMobileToken(null) : refreshTokenLoginTrigger(null));

    if (!response || 'error' in response) {
      clearAuthSession();
      bounceUnauthenticatedUser(window.location.href);
      return false;
    }

    if (response.data) {
      if (mobileApp && refreshToken.provider === 'Microsoft') {
        invokeRNMessage(ReactNativeMessageType.MicrosoftRefreshToken, {
          refreshToken: response.data.refreshToken,
        });
      }

      if (mobileApp && refreshToken.provider === 'Google') {
        const newRefreshToken = response.data.refreshToken;

        if (newRefreshToken?.trim()) {
          invokeRNMessage(ReactNativeMessageType.GoogleRefreshToken, {
            refreshToken: newRefreshToken,
          });
        }
      }

      handleLogin(
        {
          jwtToken: response.data.jwtToken,
          refreshToken: response.data.refreshToken,
          tokenProvider: refreshToken.provider,
          apiKey,
          idToken: storedAuth?.idToken,
        },
        true,
        isLoggedViaStore
      );
      return true;
    }

    return false;
  };

  const handleLoginViaStore = async () => {
    if (window.location.pathname === '/auth/callback' || window.location.pathname === ACCESS_DENIED_PATH) {
      setShouldBeLogin(false);
      return;
    }
    // TODO: F20-3137 Remove /googleoauth2 and /microsoftoauth2 after Keycloak cutover.
    if (window.location.pathname === '/microsoftoauth2') {
      setShouldBeLogin(false);
      return;
    }
    // TODO: F20-3137 Remove /googleoauth2 and /microsoftoauth2 after Keycloak cutover.
    if (window.location.pathname === '/googleoauth2') {
      setShouldBeLogin(false);
      return;
    }

    if (window.location.pathname === '/') {
      clearStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);
    }

    if (state && state.errorCode === 401) {
      const storedAuth = getStoreItem(STORE_KEYS.AUTH);
      const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
      const canRefresh = Boolean(refreshToken) || Boolean(storedAuth?.authFlow === AuthFlow.Internal && storedAuth.keycloakRefreshToken);

      if (!canRefresh) {
        logoutStoredSession(storedAuth);
        return;
      }

      const refreshSucceeded = await loginViaRefreshToken();

      if (refreshSucceeded) {
        const url = new URL(state.currentLocation);

        navigate(`${url.pathname}${url.search}${url.hash}`);
      } else {
        logoutStoredSession(storedAuth);
      }

      setShouldBeLogin(false);
      return;
    }

    const [isLogin, auth] = checkLogin();

    if (isLogin && auth) {
      handleLogin(auth, true, isLoginPath(window.location.pathname));
      return;
    }

    const [isRefreshToken, refreshToken] = checkRefreshToken();

    if (isRefreshToken && refreshToken) {
      loginViaRefreshToken();

      return;
    }

    bounceUnauthenticatedUser(window.location.href);
  };

  const handleLogout = () => {
    if (mobileApp) {
      invokeRNMessage(ReactNativeMessageType.Logout, { jwtToken });
      return;
    }

    const storedAuth = getStoreItem(STORE_KEYS.AUTH);
    const logoutUrl = createLogoutRedirect(storedAuth);

    clearEntireStore();
    clearAuthSession();

    window.location.assign(logoutUrl);
  };

  const resetApiCaches = () => {
    dispatch(coreApi.util.resetApiState());
    dispatch(flowApi.util.resetApiState());
    dispatch(heliosApi.util.resetApiState());
    dispatch(documentStorageApi.util.resetApiState());
  };

  const handleImpersonate = async (id: string) => {
    try {
      const data = await triggerStartImpersonation({ id }).unwrap();
      const impersonationJwtToken = data.jwtToken;
      if (!impersonationJwtToken) throw new Error('StartImpersonation returned no token');

      handleLogin({ jwtToken: impersonationJwtToken }, false, false, true);
      setStoreItem(STORE_KEYS.IS_IMPERSONATING, true);

      resetApiCaches();

      const decodedToken: JWTToken = jwtDecode(impersonationJwtToken);
      const userRoles = getRoleClaims(decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
      const isImpersonateUserAdmin = adminRoles.some((role) => userRoles.includes(role));

      setIsImpersonating(true);
      if (!isImpersonateUserAdmin) navigate('/user');
    } catch (error) {
      toast.error(parseHeliosError(error) || t('core.users.start_impersonation_error'));
    }
  };

  const handleUnimpersonate = async () => {
    try {
      const data = await triggerStopImpersonation().unwrap();
      const restoredJwtToken = data.jwtToken;
      if (!restoredJwtToken) throw new Error('StopImpersonation returned no token');

      setStoreItem(STORE_KEYS.IS_IMPERSONATING, false);
      setIsImpersonating(false);

      handleLogin({ jwtToken: restoredJwtToken }, false, false, true);

      resetApiCaches();
      navigate(0); // This is a hack to force a refresh of the page
    } catch (error) {
      toast.error(parseHeliosError(error) || t('core.users.stop_impersonation_error'));
    }
  };

  const handleSubstitute = (id: string) => {
    triggerSubstitute({ substitutionId: id })
      .unwrap()
      .then((data) => {
        if (!data.jwtToken) {
          toast.error(t('core.users.start_impersonation_error'));
          return;
        }
        handleLogin({ jwtToken: data.jwtToken }, false, false, false, true);
        setStoreItem(STORE_KEYS.IS_SUBSTITUTING, true);

        resetApiCaches();

        setIsSubstituting(true);
        navigate('/user', { replace: true });
      });
  };

  const handleCancelSubstitution = () => {
    triggerStopImpersonation()
      .unwrap()
      .then((data) => {
        if (!data.jwtToken) {
          toast.error(t('core.users.stop_impersonation_error'));
          return;
        }
        handleLogin({ jwtToken: data.jwtToken }, false, false, false, true);
        setStoreItem(STORE_KEYS.IS_SUBSTITUTING, false);
        setIsSubstituting(false);
        resetApiCaches();
        navigate(0); // This is a hack to force a refresh of the page
      });
  };

  useEffect(() => {
    setIsImpersonating(getStoreItem(STORE_KEYS.IS_IMPERSONATING) ?? false);
    setIsSubstituting(getStoreItem(STORE_KEYS.IS_SUBSTITUTING) ?? false);
    handleLoginViaStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoginContext.Provider
      value={{
        handleLogin,
        handleLogout,
        handleUnimpersonate,
        handleImpersonate,
        handleSubstitute,
        handleCancelSubstitution,
        user,
        permissions,
        isAdmin: permissions?.settingAdmin || permissions?.templateAdmin || permissions?.userAdmin,
        isExternal: isExternalUser(user?.user_type),
        isImpersonating,
        isSubstituting,
      }}>
      <LoadingContainer
        loading={isLoggingIn || shouldBeLogin}
        styles={{
          width: '100vw',
          height: '100vh',
        }}>
        {children}
      </LoadingContainer>
    </LoginContext.Provider>
  );
});

export { LoginContext, LoginProvider };
